
import { ElTable, ElTableColumn, ElPagination } from 'element-plus'
import { defineComponent, PropType, ref, computed, unref, watch, onMounted, render } from 'vue'

import { propTypes } from '@/utils/propTypes'
import { getSlot } from '@/utils/tsxHelper'

import { TableColumn, Pagination, TableProps, TableSlotDefault } from './GbTablesTypes'
import { array } from 'vue-types'

export default defineComponent({
    name: 'GBTables',
    props: {
        pageSize: propTypes.number.def(10),
        currentPage: propTypes.number.def(1),
        selection: propTypes.bool.def(false),
        reserveSelection: propTypes.bool.def(false),
        stripe: propTypes.bool.def(true),
        loading: propTypes.bool.def(false),
        showPaged: propTypes.bool.def(true),
        hasBorder: propTypes.bool.def(true),
        align: propTypes.string.validate((v: any) => ['left', 'center', 'right'].includes(v)).def('left'),
        headerAlign: propTypes.string.validate((v: any) => ['left', 'center', 'right'].includes(v)).def('center'),
        size: propTypes.string.validate((v: any) => ['large', 'default', 'small'].includes(v)).def('small'),
        columns: {
            type: Array as PropType<TableColumn[]>,
            default: () => []
        },
        columnWidth: propTypes.string.def('auto'),
        pagination: {
            type: Object as PropType<Pagination>,
            default: (): Pagination | undefined => undefined
        },
        data: {
            type: Array as PropType<Recordable[]>,
            default: () => []
        }
    },
    emits: ['update:pageSize', 'update:currentPage', 'register'],
    setup(props, { attrs, slots, emit, expose }) {
        const elTableRef = ref<ComponentRef<typeof ElTable>>()
        const pageSizeRef = ref(props.pageSize)
        const currentPageRef = ref(props.currentPage)
        const outsideProps = ref<TableProps>({})
        const mergeProps = ref<TableProps>({})
        const selections = ref<Recordable[]>([])

        const getProps = computed(() => {
            const propsObj = { ...props }
            Object.assign(propsObj, unref(mergeProps))
            return propsObj
        })

        const setProps = (props: TableProps = {}) => {
            mergeProps.value = Object.assign(unref(mergeProps), props)
            outsideProps.value = props
        }

        const getBindValue = computed(() => {
            const bindValue: Recordable = { ...attrs, ...props }
            delete bindValue.columns
            delete bindValue.data
            return bindValue
        })

        /* 分页配置 */
        const pagination = computed(() => {
            let pagedCfg = {
                small: true,
                background: true,
                pagerCount: 7,
                layout: 'sizes, prev, pager, next, jumper, ->, total',
                pageSizes: [10, 20, 30, 40, 50, 100],
                disabled: false,
                hideOnSinglePage: false,
                total: 10
            }

            return Object.assign(pagedCfg, unref(getProps).pagination)
        })

        /* 行样式 */
        const headerRowClassName = ({ rowIndex }: { rowIndex: number }) => {
            return 'success-row';
        }

        const selectionChange = (selection: Recordable[]) => {
            selections.value = selection
        }

        onMounted(() => {
            const tableRef = unref(elTableRef)
            emit('register', tableRef?.$parent, elTableRef)
        })

        watch(
            () => unref(getProps).pageSize,
            (val: number) => { pageSizeRef.value = val }
        )

        watch(
            () => unref(getProps).currentPage,
            (val: number) => { currentPageRef.value = val }
        )

        watch(
            () => pageSizeRef.value,
            (val: number) => {
                emit('update:pageSize', val)
            }
        )

        watch(
            () => currentPageRef.value,
            (val: number) => {
                emit('update:currentPage', val)
            }
        )

        /* 是否设置多选 */
        const renderTableSelection = () => {
            const { selection, reserveSelection, align, headerAlign } = unref(getProps)
            return selection ?
                (
                    <ElTableColumn type="selection" reserveSelection={reserveSelection} align={align} headerAlign={headerAlign} width="50"></ElTableColumn>
                ) : undefined
        }

        /* 渲染序列号 */
        const renderTableIndex = () => {
            const { align, headerAlign } = unref(getProps)
            return (<ElTableColumn type="index" align='center' fixed='left' headerAlign={headerAlign} label="序号" width="50"></ElTableColumn>)
        }

        /* 渲染表行 */
        const renderTabColumns = (columnsChildren?: TableColumn[]) => {
            const { columns, align, headerAlign, columnWidth } = unref(getProps)

            let tabColumns = (columnsChildren || columns).map((col) => {
                const props = { ...col }
                if (props.children) delete props.children

                return (
                    <ElTableColumn show-overflow-tooltip width={col.columnWidth || columnWidth} align={col.align || align} headerAlign={col.headerAlign || headerAlign} {...props} property={col.field}>
                        {{
                            default: (data: TableSlotDefault) => getSlot(slots, col.field, data) || col?.formatter?.(data.row, data.column, data.row[col.field], data.$index) || data.row[col.field] || '--',
                            header: () => getSlot(slots, `${col.field}-hd-lab`) || col.label
                        }}
                    </ElTableColumn>
                )
            });

            return [...[renderTableIndex()], ...[renderTableSelection()]].concat(tabColumns);
        }

        /* 渲染分页 */
        const renderPaged = (showPaged: boolean) => {
            return showPaged ? (
                <div style="margin-top:20px;">
                    <ElPagination v-model:pageSize={pageSizeRef.value} v-model:currentPage={currentPageRef.value} {...unref(pagination)}></ElPagination>
                </div>) : undefined;
        }

        return () => (
            <div v-loading={unref(getProps).loading}>
                <ElTable width="100%"
                    ref={elTableRef}
                    header-cell-class-name={headerRowClassName}
                    data={unref(getProps).data}
                    border={unref(getProps).hasBorder}
                    onSelection-change={selectionChange}
                    {...unref(getBindValue)} >
                    {{
                        default: () => renderTabColumns(),
                        append: () => getSlot(slots, 'append')
                    }}
                </ElTable>
                {renderPaged(unref(getProps).showPaged)}
            </div>
        )
    }
})
